import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Form } from "antd";
import dayjs from "dayjs";
import { FaFileAlt } from "react-icons/fa";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { GlobalTable } from "../../../global/components/GlobalTable";
import useGet from "../../../global/hooks/useGet";
import { hasRole } from "../../../global/hooks/getUserRole";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { convertToUnix } from "../../../global/utils/unixToLocalTime";
import { OnSiteRegisterFilter } from "../Components/FilterComponent";
import { RegisterReportForm } from "../Components/RegisterReportForm";
import { OnSiteRegisterColumns } from "./Columns";

const initialFilterState = {
  RegisterNo: "",
  RegisterTime: "",
  OemNo: "",
  DealerNo: "",
  Mac: "",
};

export const OnSiteRegister = () => {
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "RegisterNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [filter, setFilter] = useState(initialFilterState);
  const [reportOpen, setReportOpen] = useState(false);
  const [formFilter] = Form.useForm();

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/registers/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/registers/`;
  const { data, loading, refetch } = useGet(url);

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const { data: optionsData } = useGet(optionUrl);
  const modifiedOems = getModifiedResponse(optionsData, "oems");
  const modifiedDealers = getModifiedResponse(optionsData, "dealers");

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }
    setFilter((prev) => ({ ...prev, ...matchedFilter }));
  }, []);

  useEffect(() => {
    if (modParams?.RegisterTime) {
      formFilter.setFieldsValue({
        RegisterTime: dayjs.unix(Number(modParams.RegisterTime)),
      });
    }
  }, [params]);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({ ...pagination, current: page, pageSize });
      setParams({
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page,
      });
    },
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFormChange = (changedValues) => {
    if (changedValues.RegisterTime !== undefined) {
      setFilter((prev) => ({
        ...prev,
        RegisterTime: changedValues.RegisterTime,
      }));
    }
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] =
          key === "RegisterTime" && typeof filter[key] === "object"
            ? convertToUnix(filter[key])
            : filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";
    if (isEmpty) {
      handleReset();
    } else {
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams({
        limit: 10,
        sort_by: "RegisterNo",
        order: "desc",
        page: 1,
        ...filterDict,
      });
      refetch();
    }
  };

  const handleReset = () => {
    formFilter.resetFields();
    setFilter(initialFilterState);
    setPagination({ current: 1, pageSize: 10, total: 0 });
    setParams({ limit: 10, sort_by: "RegisterNo", order: "desc" });
    refetch();
  };

  useEffect(() => {
    if (data && !loading) {
      setListData(
        data?.data?.map((el) => ({
          ...el,
          key: el?.RegisterNo,
        })) || [],
      );
    }
  }, [data, loading]);

  const breadcrumbItems = [
    { label: "On Site Register", href: "/on-site-register" },
  ];

  const buttonsData = hasRole(1, 2, 3)
    ? [
        {
          label: "Report",
          icon: <FaFileAlt className="!text-[14px] font-medium" />,
          onClick: () => setReportOpen(true),
        },
      ]
    : [];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <OnSiteRegisterFilter
          formFilter={formFilter}
          handleFormChange={handleFormChange}
          filter={filter}
          setFilter={setFilter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          modifiedOems={modifiedOems}
          modifiedDealers={modifiedDealers}
        />
        <GlobalTable
          columns={OnSiteRegisterColumns(setParams, params)}
          dataSource={listData}
          pagination={paginationConfig}
          loading={loading}
          resize
        />
      </div>
      {reportOpen && (
        <RegisterReportForm
          open={reportOpen}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
};
